import {Component, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {
    CustomerContactDto,
    CustomerService,
    VisitorContactDto
} from '../../../_services/configuration-services';
import {ContactForm} from '../customer-data-form/customer-data-form.component';
import {SearchCustomerComponent} from '../search-customer/search-customer.component';
import {firstValueFrom} from 'rxjs';

@Component({
    selector: 'app-customer-visitor-selector',
    templateUrl: './customer-visitor-selector.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CustomerVisitorSelectorComponent implements OnInit {

    @Input() debtorType:  FormControl<DebtorType>;
    @Input() customerIdControl: FormControl<string>;
    @Input() visitorContactForm: FormGroup<ContactForm> = null!;

    @Input() isExploitant = false; // Depending on context (and not only the roles assigned to the user)

    @Input() customerId: string | null = null;
    @Input() visitor: VisitorContactDto | null = null;
    @Input() customer: CustomerContactDto | null = null;

    @Output() debtorTypeChange = new EventEmitter<DebtorType>();

    @ViewChild('searchCustomerComponent') searchCustomer: SearchCustomerComponent;

    DebtorType = DebtorType;

    customerContact: CustomerContactDto | null = null;

    constructor(private readonly customerService: CustomerService) {

    }

    ngOnInit(): void {
        if (this.customerId) {
            this.debtorType.setValue(DebtorType.Customer);

            if (this.customer) {
                // Editing a booking: the customer contact is already embedded in the booking read
                // model, so there is nothing to load — and we avoid getCustomer (forbidden for an
                // exploitant without the GestionClients permission).
                this.setSelectedCustomer(this.customer);
            } else {
                // A logged-in customer is booking for themselves.
                void this.loadConnectedCustomer();
            }

        } else if (this.isExploitant && !this.visitor) {
            // Backoffice creating a booking: the debtor is chosen via the search field below.
            this.debtorType.setValue(DebtorType.Customer);

        } else {
            this.debtorType.setValue(DebtorType.Visitor);
        }
    }

    changeDebtorType(newDebtorType: DebtorType): void {
        this.debtorType.setValue(newDebtorType);

        if (newDebtorType == DebtorType.Customer) {
            this.customerIdControl.enable();
            this.visitorContactForm.disable();

        } else {
            this.visitorContactForm.enable();
            this.customerIdControl.disable();
        }

        this.debtorTypeChange.emit(newDebtorType);
    }

    async loadConnectedCustomer(): Promise<void> {
        const customer = await firstValueFrom(this.customerService.getMe());

        this.setSelectedCustomer(CustomerContactDto.fromJS({
            id: customer.id,
            email: customer.email,
            phone: customer.phoneMobile ?? customer.phonePrivate ?? customer.phoneProfessional ?? '', // Phone is not required for customer
            firstName: customer.firstName,
            lastName: customer.lastName,
            city: customer.city,
            country: customer.country,
            street: customer.street,
            postalCode: customer.postalCode
        }));
    }

    getSelectedCustomer(): CustomerContactDto {
        return this.customerContact;
    }

    setSelectedCustomer(customer: CustomerContactDto): void {
        if (customer !== null) {
            this.customerContact = customer;
            this.customerContact.phone = this.customerContact.phone ?? ''; // Phone is not required for user
            this.customerIdControl.setValue(customer.id);
        }
    }
}

export enum DebtorType {
    Customer = 'Customer',
    Visitor = 'Visitor'
}
